/**
 * Session cache for personal and club hunt stats.
 * Callers hydrate UI synchronously, then revalidate when the entry is stale.
 */

export const USER_STATS_KEY = 'user:stats';
export const USER_HUNTS_KEY = 'user:hunts';
export const STATS_STALE_MS = 5 * 60 * 1000;

/**
 * Season must be part of the key. Without it, switching seasons reads the
 * previous season's numbers back out of the cache for up to STATS_STALE_MS.
 */
export function clubStatsKey(clubId, season = '') {
  return season ? `club:${clubId}:stats:${season}` : `club:${clubId}:stats`;
}

export function clubHarvestsKey(clubId, season = '') {
  return season ? `club:${clubId}:harvests:${season}` : `club:${clubId}:harvests`;
}

export function hasStatsBreakdowns(data) {
  return (
    Array.isArray(data?.time_period_breakdown) ||
    Array.isArray(data?.weather_breakdown) ||
    Array.isArray(data?.temperature_breakdown) ||
    Array.isArray(data?.moon_phase_breakdown)
  );
}

function readEntry(key, isValid) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isValid(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getCachedEntry(key, isValid, { allowStale = true, now = Date.now() } = {}) {
  const entry = readEntry(key, isValid);
  if (!entry) return null;
  if (!allowStale && now - Number(entry.ts || 0) > STATS_STALE_MS) return null;
  return entry.data;
}

function isCachedEntryFresh(key, isValid, now = Date.now()) {
  const entry = readEntry(key, isValid);
  if (!entry) return false;
  return now - Number(entry.ts || 0) <= STATS_STALE_MS;
}

export function getCachedStats(key, options) {
  return getCachedEntry(key, hasStatsBreakdowns, options);
}

export function isCachedStatsFresh(key, now = Date.now()) {
  return isCachedEntryFresh(key, hasStatsBreakdowns, now);
}

export function normalizeHuntsList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return null;
}

export function getCachedHunts({ allowStale = true, now = Date.now() } = {}) {
  return getCachedEntry(USER_HUNTS_KEY, Array.isArray, { allowStale, now });
}

export function isCachedHuntsFresh(now = Date.now()) {
  return isCachedEntryFresh(USER_HUNTS_KEY, Array.isArray, now);
}

export function getCachedClubHarvests(clubId, season = '', options) {
  if (clubId == null || clubId === '') return null;
  return getCachedEntry(clubHarvestsKey(clubId, season), Array.isArray, options);
}

export function isCachedClubHarvestsFresh(clubId, season = '', now = Date.now()) {
  if (clubId == null || clubId === '') return false;
  return isCachedEntryFresh(clubHarvestsKey(clubId, season), Array.isArray, now);
}

export function setCachedClubHarvests(clubId, season, data) {
  if (clubId == null || clubId === '') return;
  const rows = normalizeHuntsList(data);
  if (!rows) return;
  try {
    sessionStorage.setItem(
      clubHarvestsKey(clubId, season),
      JSON.stringify({ data: rows, ts: Date.now() })
    );
  } catch {
    // sessionStorage may be unavailable
  }
}

export function setCachedHunts(data) {
  const rows = normalizeHuntsList(data);
  if (!rows) return;
  try {
    sessionStorage.setItem(USER_HUNTS_KEY, JSON.stringify({ data: rows, ts: Date.now() }));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function setCachedStats(key, data) {
  if (!data || !hasStatsBreakdowns(data)) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function invalidateUserStatsCache() {
  try {
    sessionStorage.removeItem(USER_STATS_KEY);
    sessionStorage.removeItem(USER_HUNTS_KEY);
  } catch {
    // ignore
  }
}
